import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Start a Monthly Giving Page as a Missionary (Without Becoming a 501(c)(3))';
const DESCRIPTION =
    'You don’t need to form your own nonprofit to receive monthly support. Three legitimate paths compared — sending agency, fiscal sponsor, and direct platform — with the trade-offs nobody tells you up front.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover6.jpg',
            datePublished: '2026-07-20',
            dateModified: '2026-07-20',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-start-monthly-giving-page-without-501c3',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Do I need a 501(c)(3) to receive monthly missionary support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. Most missionaries receive support through their sending agency's existing 501(c)(3) status, a fiscal sponsor, or directly through a giving platform — without ever forming their own nonprofit.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Are donations to a direct giving platform tax-deductible?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Generally no. Direct platforms like Devotion Fund treat gifts as personal support, not tax-deductible charitable donations. Donors who need a tax-deductible receipt should give through your sending agency or a fiscal sponsor instead.',
                    },
                },
                {
                    '@type': 'Question',
                    name: "What's the fastest way to start receiving support without an agency?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A direct giving platform. There's no onboarding vetting or waiting period the way there is with a fiscal sponsor — you can set up a monthly giving page and start receiving support the same week.",
                    },
                },
            ],
        },
    ],
};

export default function HowToStartAMonthlyGivingPageWithout501c3() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-start-monthly-giving-page-without-501c3' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-monthly-giving-no-501c3-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover6.jpg" alt="Hands holding an open notebook, planning a path forward" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>The most common question I get from missionaries setting up support raising for the first time: "Do I need to file as a 501(c)(3) before my friends can give me money?"</p>

                    <p>No. And you almost certainly shouldn't try. Forming a nonprofit takes 6–18 months, $1,500–$5,000 in legal and filing fees, and an ongoing administrative burden you don't have time for. Worse, if you're heading to the field within a year, you'll spend most of your prep window on paperwork instead of building support.</p>

                    <p>There are three legitimate paths for receiving monthly support without forming your own 501(c)(3). Here's what each one actually involves, when it's the right call, and the trade-offs nobody tells you about up front.</p>

                    <h2>Path 1: Receive support through your sending agency</h2>

                    <p><strong>How it works:</strong> Your agency (Cru, InterVarsity, Navigators, Pioneers, OMF, AIM, your denomination's missions board, etc.) holds 501(c)(3) status and acts as the fiscal entity. Donors give to the agency, designate the gift to your account, and receive a tax-deductible receipt. The agency takes an administrative percentage (typically 8–15%) and disburses the rest to you as salary.</p>

                    <p><strong>When it's right:</strong></p>
                    <ul>
                        <li>You're already going through agency onboarding anyway.</li>
                        <li>You want the legal, tax, and HR infrastructure handled for you.</li>
                        <li>Your donors care about tax deductibility (typically larger U.S. donors).</li>
                    </ul>

                    <p><strong>The trade-offs:</strong></p>
                    <ul>
                        <li>You pay 8–15% in admin fees — meaningfully more than any modern platform.</li>
                        <li>You don't own the donor relationship. The agency does. If you leave, the donor list stays with them.</li>
                        <li>Your update cadence and donor communication are constrained by agency policy.</li>
                    </ul>

                    <p>This is the right path for most pre-field missionaries going through Cru, IV, Navs, or a denominational board. The infrastructure is worth the fee.</p>

                    <h2>Path 2: Receive support through a fiscal sponsor</h2>

                    <p><strong>How it works:</strong> A fiscal sponsor is a 501(c)(3) whose entire purpose is to receive tax-deductible donations on behalf of individual missionaries and ministries that don't have their own nonprofit status. Modern Day Missions and Reliant Mission are two of the best-known in the U.S. Donors give to the sponsor, designate you, and receive a tax-deductible receipt. The sponsor takes an admin fee.</p>

                    <p><strong>When it's right:</strong></p>
                    <ul>
                        <li>You're not going through a traditional agency but want tax-deductible donor receipts.</li>
                        <li>A meaningful share of your U.S. donors itemize their taxes.</li>
                        <li>You want lower fees than a traditional agency but more legal structure than a personal account.</li>
                    </ul>

                    <p><strong>The trade-offs:</strong></p>
                    <ul>
                        <li>Fiscal sponsors have governance restrictions. Funds must be used for charitable ministry purposes, documented — you can't just transfer money to your personal account and use it however.</li>
                        <li>Onboarding takes 30–90 days, with vetting (statement of faith, ministry plan, references).</li>
                        <li>You're still not the direct legal recipient. The sponsor is.</li>
                    </ul>

                    <p>This is the strongest path for U.S. missionaries with itemizing donors who don't have an agency relationship. For everyone else, it's overkill.</p>

                    <h2>Path 3: Receive support directly through a modern giving platform</h2>

                    <p><strong>How it works:</strong> You receive monthly support directly to your personal bank account through a platform like Devotion Fund, Patreon, Continue to Give, or GiveSendGo. Donations are <em>not</em> tax-deductible. You handle the income on your personal taxes as self-employment income (or as gifts, depending on structure — talk to an accountant about your specific case).</p>

                    <p><strong>When it's right:</strong></p>
                    <ul>
                        <li>Most of your donors are family, friends, and church relationships who aren't giving to itemize anyway. (For donations under ~$5,000/year per household, the tax-deductibility math usually doesn't change behavior.)</li>
                        <li>You want direct control of your donor list, your update cadence, and your platform.</li>
                        <li>You want the lowest possible all-in fee.</li>
                        <li>You're a non-U.S. missionary where the U.S. tax-deductibility question doesn't apply.</li>
                    </ul>

                    <p><strong>The trade-offs:</strong></p>
                    <ul>
                        <li>No tax-deductibility for your donors. Be honest about this in your ask.</li>
                        <li>You handle your own self-employment tax bookkeeping. Set aside 25–30% for taxes.</li>
                        <li>No third-party legitimacy signal — your donors are trusting you, not a nonprofit.</li>
                    </ul>

                    <p>This is the right path for the majority of missionaries today, in my honest opinion. The tax-deductibility question matters less than missionary culture pretends it does — most $25–$100/month givers don't itemize, and the donors who do itemize tend to give to your sending agency or fiscal sponsor anyway, separately from your personal page.</p>

                    <h2>How to decide — a quick filter</h2>

                    <ol>
                        <li><strong>Are you already going through a sending agency?</strong> → Path 1. Stop reading. The agency handles it.</li>
                        <li><strong>Do you expect more than 30% of your support to come from itemizing U.S. donors giving $1,000+/year?</strong> → Path 2 (fiscal sponsor) is worth the friction.</li>
                        <li><strong>Otherwise?</strong> → Path 3 (direct platform). It's the simplest, the cheapest, and what most modern missionaries actually need.</li>
                    </ol>

                    <h2>The "monthly giving page" specifically — what you need to set up</h2>

                    <p>Regardless of which path you choose, your page needs five things:</p>
                    <ol>
                        <li><strong>A real photo.</strong> Of you. Not a stock missions image. People give to people.</li>
                        <li><strong>A specific monthly target.</strong> "$3,200/month for 24 months" reads as planned and trustworthy. "Any support is appreciated" reads as drift.</li>
                        <li><strong>A one-paragraph "why" in your own voice.</strong> Where you're going, who you'll work with, what you'll do day to day.</li>
                        <li><strong>A clear recurring-give CTA.</strong> Not "donate" — "partner monthly." The word matters.</li>
                        <li><strong>An update cadence commitment.</strong> "I'll send a real update once a month" — and then do it.</li>
                    </ol>

                    <p>The mechanics of <em>which</em> platform hosts the page matter less than these five. Devotion Fund builds them in by default; you can build them on any platform if you put in the work.</p>

                    <h2>What we'd recommend trying first</h2>

                    <p>If you're in U.S. agency onboarding: use your agency's giving page and skip the rest of this. Done.</p>

                    <p>If you're not, and you want to start receiving monthly support this month (not in six months when paperwork clears): set up a direct-platform page. <a href="/">Devotion Fund</a> is built specifically for this case at a 5-7% all-in fee (5% bank transfer, 7% card). If tax-deductibility becomes important later, a fiscal sponsor can run alongside, not instead of, your direct page.</p>

                    <p>The bigger mistake than picking the wrong platform is spending a year on paperwork instead of building relationships with your first 30 supporters. Start small, start now, fix the structure later.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Do I need a 501(c)(3) to receive monthly missionary support?</strong><br />
                    No. Most missionaries receive support through their sending agency's existing 501(c)(3) status, a fiscal sponsor, or directly through a giving platform — without ever forming their own nonprofit.</p>

                    <p><strong>Are donations to a direct giving platform tax-deductible?</strong><br />
                    Generally no. Direct platforms like Devotion Fund treat gifts as personal support, not tax-deductible charitable donations. Donors who need a tax-deductible receipt should give through your sending agency or a fiscal sponsor instead.</p>

                    <p><strong>What's the fastest way to start receiving support without an agency?</strong><br />
                    A direct giving platform. There's no onboarding vetting or waiting period the way there is with a fiscal sponsor — you can set up a monthly giving page and start receiving support the same week.</p>

                    <h2>Next reads</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a> — once your page is live, the letter is what actually fills it.</li>
                        <li><a href="/resources/true-cost-missionary-fundraising-platforms-2026">The True Cost of Every Missionary Fundraising Platform in 2026</a> — an honest, all-in fee comparison of seven platforms.</li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund. We're not tax attorneys, and nothing here is legal or tax advice — talk to a CPA about your specific situation. What we have done is sit through enough missionary support-raising conversations to know that the 501(c)(3) question is almost always the wrong first question, and that the right answer for most missionaries today is much simpler than they think.</p>
                    <p><small><em>Last updated: 2026-07-20.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-start-monthly-giving-page-without-501c3" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
